'use client';

import React, { useState } from 'react';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Node } from '@contentful/rich-text-types';
import a from 'indefinite';
import { NextPage } from 'next';
import NextImage, { ImageLoaderProps } from 'next/image';

import { KnotEntry } from '../../api/contentful/getKnot';
import { Breadcrumbs } from '../../components/Breadcrumbs/Breadcrumbs';
import { Container } from '../../components/Container/Container';
import { Heading } from '../../components/Heading/Heading';
import { Text } from '../../components/Text/Text';
import { ContentfulResizeTypes, getContentfulLoader } from '../../helpers/imageLoaders/contentful';

import { StepsSlider } from './components/StepsSlider';

export interface IKnotProps {
  knot: KnotEntry;
}

const richTextOptions = {
  renderNode: {
    [BLOCKS.OL_LIST]: (_node: Node, children: React.ReactNode) => {
      return <ol className="list-decimal">{children}</ol>;
    },
    [BLOCKS.LIST_ITEM]: (_node: Node, children: React.ReactNode) => {
      return <li className="text-lg">{children}</li>;
    },
    [BLOCKS.PARAGRAPH]: (_node: Node, children: React.ReactNode) => {
      return <>{children}</>;
    },
  },
};

export const Knot: NextPage<IKnotProps> = ({ knot: { fields } }) => {
  const [activeStep, setActiveStep] = useState<number>(1);

  const aspectWidth = fields.images[0]?.fields.file?.details?.image?.width ?? 200;
  const ASPECTHEIGHT = fields.images[0]?.fields.file?.details?.image?.height ?? 200;

  const contentfulLoader = ({ src, width, quality }: ImageLoaderProps): string =>
    getContentfulLoader(src, width, aspectWidth, ASPECTHEIGHT, {
      quality,
      resize: ContentfulResizeTypes.FILL,
    });

  return (
    <Container>
      <div className="m-auto mb-12 py-10" style={{ maxWidth: '940px' }}>
        <Breadcrumbs items={[{ label: 'Knots', link: '/knots' }, { label: fields.name }]} />
        <div className="mt-4 mb-16">
          {fields.images.map(
            (image, index) =>
              image?.fields.file?.url && (
                <NextImage
                  key={image.sys.id}
                  alt={image.fields.title || 'knots'}
                  className={`mb-3 size-full object-center ${activeStep === index + 1 ? '' : 'hidden'}`}
                  src={image.fields.file.url}
                  width={image.fields.file.details.image?.width}
                  height={image.fields.file.details.image?.height}
                  loader={contentfulLoader}
                />
              ),
          )}

          <StepsSlider
            numberOfSteps={fields.images.length}
            onChange={value => {
              setActiveStep(value as number);
            }}
          />
          <div className="mt-8 flex w-full">
            <div className="border-twilight m-auto rounded-sm p-3 text-xs select-none">
              Drag slider to animate
            </div>
          </div>
        </div>

        <Heading as="h1" className="mb-2">
          {`How to tie ${a(fields.name)}`}
        </Heading>
        <Heading as="h3" variant="sm" className="mb-4">
          {fields.shortDescription}
        </Heading>
        <Text className="mb-12 italic">{fields.description}</Text>

        <Heading variant="md" className="mb-4">
          Step by step
        </Heading>
        <div className="pl-8">{documentToReactComponents(fields.steps, richTextOptions)}</div>
      </div>
    </Container>
  );
};
